import { FC, useEffect, useState } from 'react'
import styled from 'styled-components'
import { MileageHistoryItemResponseDto } from '@/apis/rest/generated/types/mileageHistoryItemResponseDto'
import { ImageAsset, TextLegacy } from '@/components/common'
import { NewRow } from '@/components/common/layouts/NewRow'
import { LayoutHeader } from '@/components/domains/layouts'
import { ListUserMileageRecords } from '@/components/domains/list'
import { useCustomRouter } from '@/containers/hooks'
import { useAfterLogin } from '@/containers/hooks/auth'
import { UserContainer } from '@/containers/users/UserContainer'
import { useMileageHistories } from '@/pages/mypage/mileages/hooks/use-mileage-histories'
import { useMileageInfo } from '@/pages/mypage/mileages/hooks/use-mileage-info'
import { currencyText } from '@/utils/utilNumber'

const PAGE_SIZE = 20

const PageMyPageMileageRecords: FC = () => {
  const [page, setPage] = useState(1)
  const { isReady } = useCustomRouter()
  const { me, meIsLoading } = UserContainer.useContainer()
  const { loginOperator } = useAfterLogin()

  /**
   * 비로그인 상태로 직접 진입 시 로그인 처리 후 현재 페이지로 복귀.
   * loginOperator 가 앱/웹을 분기 처리한다.
   * - 앱: 네이티브 로그인 → 성공 후 현재 페이지 reload (로그인 상태로)
   * - 웹: 로그인 페이지 replace 이동 → return_url(현재 경로)로 복귀
   *   (replace 로 이동해야 로그인 후 뒤로가기 시 마일리지 페이지 이전 화면으로 정상 복귀)
   */
  useEffect(() => {
    if (isReady && !meIsLoading && !me) {
      loginOperator(undefined, true, undefined, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, meIsLoading, me])

  const { histories, isFetching, pageInfo } = useMileageHistories({
    page,
    size: PAGE_SIZE,
  })

  const [accumulatedHistories, setAccumulatedHistories] = useState<MileageHistoryItemResponseDto[]>([])

  useEffect(() => {
    if (histories && histories.length > 0) {
      setAccumulatedHistories((prev) => {
        // 1페이지면 초기화(새로고침/필터 변경 대비), 2페이지부터는 기존 데이터 뒤에 누적
        return page === 1 ? histories : [...prev, ...histories]
      })
    }
  }, [histories, page])

  const hasNextPage = pageInfo?.hasNextPage ?? false

  const handleLoadMore = () => {
    setPage((prev) => prev + 1)
  }

  return (
    <>
      <LayoutHeader />
      <StyledContainer>
        <RenderSummary />
        <ListUserMileageRecords
          histories={accumulatedHistories || []}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetching}
          onLoadMore={handleLoadMore}
        />
      </StyledContainer>
    </>
  )
}

const RenderSummary = () => {
  const { me, meIsLoading } = UserContainer.useContainer()

  const { mileageInfo, isLoading } = useMileageInfo()

  if (meIsLoading || !me?.id || isLoading) {
    return null
  }

  const mileage = mileageInfo?.point ?? 0
  const expectedExpiryPoint = mileageInfo?.expectedExpiryPoint ?? 0

  return (
    <>
      <StyledSummary>
        <NewRow justify="center">
          <TextLegacy weight="bold">보유 적립금</TextLegacy>
        </NewRow>
        <NewRow justify="center" align="center">
          <ImageAsset name="mileage" size={32} />
          <TextLegacy size={{ xs: 30, md: 32 }} color="blue500" weight="bold">
            {currencyText(mileage)}
          </TextLegacy>
        </NewRow>
        <NewRow justify="center">
          <TextLegacy size={{ xs: 12, md: 14 }}>{`7일 이내 소멸 예정 적립금 : ${currencyText(
            expectedExpiryPoint
          )}`}</TextLegacy>
        </NewRow>
      </StyledSummary>
    </>
  )
}

const StyledContainer = styled.div`
  padding: 20px;
`

const StyledSummary = styled.div`
  background-color: ${(props) => props.theme.color.blue50};
  padding: 20px;
  margin-bottom: 12px;
  border-radius: 6px;
`

export default PageMyPageMileageRecords
